import type { CustomSkillRepository } from '../persistence/customSkillRepository.ts';
import type { CustomBackgroundRepository } from '../persistence/customBackgroundRepository.ts';
import type { CustomItemRepository } from '../persistence/customItemRepository.ts';
import { allContentIds } from '../content/dccCoreContent.ts';
import { makeUniqueCustomContentId } from '../domain/customContentId.ts';

/**
 * Allocating a new custom content ID, across every library.
 *
 * RULE DCC-CUSTOM-011 says the `custom:` namespace is global. The three
 * creation services used to satisfy that by reading `allContentIds()` - the
 * live COMPOSED catalogue - plus their own library. That has a hole: a
 * definition can legitimately be stored and absent from the catalogue, because
 * one of its dependencies is unresolved (RULE DCC-CUSTOM-009). It still owns
 * its ID. Handing that ID to something else because its owner happens to be
 * uncomposable today would create exactly the cross-type collision
 * DCC-CUSTOM-011 exists to prevent - and the moment the missing dependency
 * returned, BOTH definitions would be refused.
 *
 * So allocation reads the actual stored libraries, not the catalogue's view of
 * them. One place does it for all three types, rather than each service loading
 * the other two.
 *
 * FAILS CLOSED. If a sibling library cannot be read, its IDs cannot be
 * inspected, so global uniqueness cannot be claimed and allocation refuses.
 * The high-entropy generator makes a collision unlikely; correctness must not
 * rest on that.
 */

export interface IdAllocatorDeps {
  customSkills: CustomSkillRepository;
  customBackgrounds: CustomBackgroundRepository;
  customItems: CustomItemRepository;
}

export type IdAllocation =
  | { ok: true; id: string }
  | { ok: false; message: string };

/**
 * Every ID currently spoken for: the printed catalogue, whatever custom content
 * has composed, and - the part that was missing - every definition STORED in
 * any custom library, composed or not.
 */
export async function takenCustomIds(
  deps: IdAllocatorDeps,
): Promise<{ ok: true; ids: Set<string> } | { ok: false; unreadable: string[] }> {
  const [skills, backgrounds, items] = await Promise.all([
    deps.customSkills.load(),
    deps.customBackgrounds.load(),
    deps.customItems.load(),
  ]);

  const unreadable: string[] = [];
  if (skills.status === 'unreadable') unreadable.push('custom Skill');
  if (backgrounds.status === 'unreadable') unreadable.push('custom Background');
  if (items.status === 'unreadable') unreadable.push('custom Item');
  if (unreadable.length > 0) return { ok: false, unreadable };

  const ids = allContentIds();
  for (const skill of skills.library.skills) ids.add(skill.id);
  for (const background of backgrounds.library.backgrounds) ids.add(background.id);
  for (const item of items.library.items) ids.add(item.id);
  return { ok: true, ids };
}

/**
 * A new ID that is certainly not in use anywhere.
 *
 * `nextUnique` is supplied so the collision path is testable with fixed values
 * rather than by waiting for an improbable event.
 */
export async function allocateCustomId(
  name: string,
  deps: IdAllocatorDeps,
  nextUnique: () => string,
): Promise<IdAllocation> {
  const taken = await takenCustomIds(deps);
  if (!taken.ok) {
    return {
      ok: false,
      message:
        `Nothing was created. Your ${taken.unreadable.join(' and ')} file cannot be read, so ` +
        'there is no way to be sure a new ID would not clash with something already in it. ' +
        'Repair or move that file first.',
    };
  }
  return { ok: true, id: makeUniqueCustomContentId(name, taken.ids, nextUnique) };
}
